import * as types from "../mutation-types";
import { cloneObject, generateUUID } from "@/util.js";
const DASH = "&mdash;";

export function initialState() {
    return {
        reloadChartData: false,
        chartData: null
    };
}

export function calcAssetAllocation(assetClassData) {
    const totalLongWeight = Object.keys(assetClassData).reduce((prev, classId) => {
        return prev + assetClassData[classId].long;
    }, 0);
    return Object.keys(assetClassData).reduce((prev, classId) => {
        prev[classId] = {
            ...assetClassData[classId],
            rescaledLong: totalLongWeight === 0 ? 0 : (assetClassData[classId].long / totalLongWeight) * 100
        };
        return prev;
    }, {});
}

function assetClassWeightGetter(assetClassData, isUsedRescaledLong) {
    let newAssetClassData = assetClassData;
    if (isUsedRescaledLong) {
        newAssetClassData = calcAssetAllocation(assetClassData);
    }

    return assetClassId => {
        return isUsedRescaledLong ? newAssetClassData[assetClassId].rescaledLong : newAssetClassData[assetClassId].net;
    };
}

function assetClassDashWeightGetter(assetClassData, isUsedRescaledLong) {
    if (!assetClassData) {
        return () => DASH;
    }
    return assetClassWeightGetter(assetClassData, isUsedRescaledLong);
}

function getPortfolioAssetClassData(assetClassData, isUsedRescaledLong, broadGroup) {
    if (assetClassData) {
        const getBroadAssetClassWeight = assetClassWeightGetter(assetClassData, isUsedRescaledLong);
        return Object.keys(broadGroup.compItems)
            .sort((aClassId, bClassId) => {
                return broadGroup.compItems[aClassId].order - broadGroup.compItems[bClassId].order;
            })
            .reduce((prev, classId) => {
                prev.push({
                    color: broadGroup.compItems[classId].color,
                    name: broadGroup.compItems[classId].className,
                    value: getBroadAssetClassWeight(classId)
                });
                return prev;
            }, []);
    }
    return null;
}

function getNonSecData(data, isInvestmentFlow, source, getters) {
    return source.map((dataSource, index) => {
        const { id, benchmark: { includeBenchmark } = false } = dataSource;
        const groupData = isInvestmentFlow
            ? cloneObject(data.filter(item => item.id === id && item.type !== "Portfolio"))
            : cloneObject(data.filter(item => item.id === id));
        const benchmarkList = groupData.filter(item => (includeBenchmark ? true : item.type !== "Benchmark"));
        return benchmarkList.map(item => getters.getFormatedItem(item, index));
    });
}

function getInvestmentFlowData(data, source, getters, rootState) {
    const investmentData = source.map((dataSource, index) => {
        const { securityId } = dataSource;
        const groupData = data.length > 0 ? cloneObject(data.filter(item => item.id === securityId)) : [];
        return groupData.map(item => getters.getFormatedItem(item, index));
    });
    const nonSecData = getNonSecData(data, true, rootState.investments.rawPortfolioList, getters);
    return investmentData.concat(nonSecData).flat();
}

const state = {
    ...initialState()
};

const getters = {
    getInvestments: (state, getters, rootState) => data => {
        const isInvestmentFlow = rootState.hypo && rootState.hypo.hypoType ? parseInt(rootState.hypo.hypoType) === 2 : false;
        const source = isInvestmentFlow ? cloneObject(rootState.investments.rawPortfolioList[0].securities) : rootState.investments.rawPortfolioList;
        source.length = isInvestmentFlow && source.length > 5 ? 5 : source.length;
        return isInvestmentFlow ? getInvestmentFlowData(data, source, getters, rootState) : getNonSecData(data, false, source, getters).flat();
    },
    getFormatedItem: (state, getters, rootState, rootGetters) => (item, index) => {
        const isInvestmentFlow = rootState.hypo && rootState.hypo.hypoType ? parseInt(rootState.hypo.hypoType) === 2 : false;
        const { portfolioColor, benchmarkColor } = rootGetters["config/colorConfig"];
        const investmentTypes = ["NetInvestment", "Holding"];
        const InvestmentColors = ["Portfolio", "Holding"];
        // filter by portfolioList
        const portfolioColors = portfolioColor.split(";");
        const portfolioColorsLength = portfolioColors.length;
        const benchmarkColors = benchmarkColor.split(";");
        const benchmarkColorsLength = benchmarkColors.length;
        const type = item.type;
        item.name = investmentTypes.indexOf(type) < 0 ? getters.formatLabel(`${type} ${index + 1}`) : item.name;
        item.color = InvestmentColors.indexOf(type) >= 0 ? portfolioColors[index % portfolioColorsLength] : benchmarkColors[index % benchmarkColorsLength];
        item.uuid = generateUUID();
        // Remove after two benchmarks is implemented
        if (isInvestmentFlow && type === "Benchmark") {
            item.name = "Benchmark";
        }
        return item;
    },
    portfolioDateRange(state, getters) {
        let startDate = "",
            endDate = "";
        getters.growthChartData.forEach(item => {
            item.data.forEach(i => {
                startDate = startDate && startDate < new Date(i.date) ? startDate : new Date(i.date);
                endDate = endDate && endDate > new Date(i.date) ? endDate : new Date(i.date);
            });
        });
        return {
            startDate,
            endDate
        };
    },
    growthChartData: (state, getters, rootState, rootGetters) => {
        let result = [];
        if (state.chartData && state.chartData.growth) {
            const netData = [];
            const { netColor } = rootGetters["config/colorConfig"];
            getters.getInvestments(state.chartData.growth).forEach(d => {
                const groupData = Object.keys(d.serials).map(dateString => {
                    const date = new Date(dateString);
                    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                    return {
                        value: d.serials[dateString],
                        date: new Date(date.getTime() + userTimezoneOffset)
                    };
                });
                if (d.type === "NetInvestment") {
                    const netInvestment = {
                        name: d.name,
                        color: netColor,
                        data: groupData
                    };
                    netData.push(netInvestment);
                } else {
                    result.push({
                        name: getters.formatLabel(d.name),
                        color: d.color,
                        data: groupData
                    });
                }
            });
            // https://msjira.morningstar.com/browse/HYPO-1553
            if (1 === rootState.investments.rawPortfolioList.length) {
                result = result.concat(netData);
            }
        }
        result.sort((itemX, itemY) => {
            const lastValX = itemX.data[itemX.data.length - 1].value;
            const lastValY = itemY.data[itemY.data.length - 1].value;
            if (lastValX < lastValY) {
                return 1;
            }
            if (lastValX > lastValY) {
                return -1;
            }
            return 0;
        });
        return result;
    },
    growthChartDottedLines: (state, getters) => {
        return state.chartData && state.chartData.growth
            ? getters
                  .getInvestments(state.chartData.growth)
                  .filter(d => d.type === "Benchmark")
                  .map(d => d.name)
            : [];
    },
    performanceData: state => {
        const { chartData } = state;
        if (!chartData) {
            return null;
        }
        const { performanceData = {} } = chartData;
        return performanceData;
    },
    investments: state => {
        const { chartData } = state;
        if (!chartData) {
            return null;
        }
        const { investments = {} } = chartData;
        return investments;
    },
    holdingsTableData: state => {
        const { chartData } = state;
        if (!chartData) {
            return null;
        }
        const { weights = [] } = chartData;
        return weights;
    },
    annualReturnData: (state, getters) => {
        if (!getters.performanceData) {
            return [];
        } else {
            const data = getters.getInvestments(getters.performanceData.annualReturn);
            // get all timePeriods and sort it
            const timePeriods = [];
            data.forEach(item => {
                item.data.forEach(i => {
                    if (timePeriods.indexOf(i.timePeriod) === -1) {
                        timePeriods.push(i.timePeriod);
                    }
                });
            });
            timePeriods.sort((a, b) => a - b);
            // supplement timePeriods
            if (data.length > 0) {
                data[0].data = timePeriods.map(tp => {
                    const item = data[0].data.filter(item => item.timePeriod === tp).pop();
                    return item ? item : { timePeriod: tp, value: null };
                });
            }
            return data;
        }
    },
    trailingReturnData: (state, getters) => {
        if (!getters.performanceData) {
            return [];
        } else {
            const rawData = getters.getInvestments(getters.performanceData.trailingReturn);
            const startDate = getters.portfolioDateRange.startDate;
            const endDate = getters.portfolioDateRange.endDate;
            const monthDiff = (endDate.getFullYear() - startDate.getFullYear() - 1) * 12 + (12 - startDate.getMonth()) + (endDate.getMonth() + 1);
            /**
             * if time range < 3 Year, show [1 Month/3 Month/6 Month/1 Year/2 Year]
             * if time range >= 3 years, show [3 Month/1 Year/3 Year/5 Year/10 Year]
             */
            return rawData.map(item => {
                const data = item.data.filter(
                    i =>
                        i.timePeriod <= monthDiff &&
                        ((monthDiff < 36 && [1, 3, 6, 12, 24].includes(i.timePeriod)) || ([3, 12, 36, 60, 120].includes(i.timePeriod) && i.timePeriod <= 120))
                );
                return {
                    ...item,
                    name: getters.formatLabel(item.name),
                    data
                };
            });
        }
    },
    assetAllocationBarData: (state, getters) => {
        // chart portfolio level asset allocation calculation data always get from aws.
        const assetAllocationBarData = {};
        const chartData = state.chartData;
        if (chartData && chartData.assetAllocation && chartData.assetAllocation.assetGroups.length > 0) {
            const broadGroup = chartData.assetAllocation.assetGroups.filter(item => item.groupType === 0)[0];
            const broadGroupId = broadGroup.groupId;
            const broadAssetClassData = chartData.assetAllocation.assetDatas[broadGroupId];
            const isUsedRescaledLong = broadGroup.chartType === 1; //AWS admin tool => 0:Bar Chart 1:Pie Chart 2:Simple Pie or Bar Chart
            const assetClassData = getters.getInvestments(broadAssetClassData);
            const portfoliosAssetClassData = assetClassData.filter(i => i.type === "Portfolio");
            portfoliosAssetClassData.forEach((item, index) => {
                const portfolioId = item.id;
                const benchmark = assetClassData.find(i => i.type === "Benchmark" && i.id === portfolioId);
                let benchmarkAAData = null;
                assetAllocationBarData[portfolioId] = {};
                const portfolioAAData = getPortfolioAssetClassData(item.assetClassData, isUsedRescaledLong, broadGroup);
                if (benchmark && benchmark.assetClassData) {
                    benchmarkAAData = getPortfolioAssetClassData(benchmark.assetClassData, isUsedRescaledLong, broadGroup);
                }

                if (portfolioAAData) {
                    assetAllocationBarData[portfolioId].portfolio = portfolioAAData;
                }
                if (benchmarkAAData) {
                    assetAllocationBarData[portfolioId].benchmark = benchmarkAAData;
                }
            });
        }
        return assetAllocationBarData;
    },
    assetAllocationReleaseDate: state => {
        const chartData = state.chartData;
        let date = "";
        if (chartData && chartData.assetAllocation && chartData.assetAllocation.releaseDate) {
            const temp = chartData.assetAllocation.releaseDate;
            date = new Date(temp);
        }
        return date !== "" ? date.toLocaleDateString() : null;
    },
    holdingsDate: state => {
        const chartData = state.chartData;
        let date = "";
        if (chartData && chartData.weights && chartData.weights.length > 0) {
            const holdingsDate = chartData.weights[0].enddate;
            date = new Date(holdingsDate);
        }
        return date !== "" ? date.toLocaleDateString("en-US", { timeZone: "UTC" }) : null;
    },
    detailAssetClassData: (state, getters) => {
        const chartData = state.chartData;
        // if detailAssetGroup doesn't exist, return empty rows
        let hasDetailedAssetClass = false;
        if (chartData && chartData.assetAllocation && chartData.assetAllocation.assetGroups.length > 0) {
            const assetGroups = chartData.assetAllocation.assetGroups;
            const detailAssetGroup = assetGroups.find(item => item.groupType === 1);
            if (detailAssetGroup !== undefined) {
                hasDetailedAssetClass = true;
            }
        }

        if (hasDetailedAssetClass) {
            const assetGroups = chartData.assetAllocation.assetGroups;
            const assetDatas = chartData.assetAllocation.assetDatas;
            const broadAssetGroup = assetGroups.find(item => item.groupType === 0);
            const detailAssetGroup = assetGroups.find(item => item.groupType === 1);

            const broadAssetDatas = getters.getInvestments(assetDatas[broadAssetGroup.groupId]);
            const detailAssetDatas = getters.getInvestments(assetDatas[detailAssetGroup.groupId]);

            const isUsedRescaledLong = broadAssetGroup.chartType === 1; //AWS admin tool => 0:Bar Chart 1:Pie Chart 2:Simple Pie or Bar Chart
            const broadAssetClasses = Object.keys(broadAssetGroup.compItems)
                .sort((aAssetClassId, bAssetClassId) => {
                    return broadAssetGroup.compItems[aAssetClassId].order - broadAssetGroup.compItems[bAssetClassId].order;
                })
                .map(assetClassId => {
                    let broadAssetClass = {
                        assetClassId,
                        name: broadAssetGroup.compItems[assetClassId].className,
                        color: broadAssetGroup.compItems[assetClassId].color
                    };

                    broadAssetClass = broadAssetDatas.reduce((prev, broadAssetData, index) => {
                        const getBroadAssetClassWeight = assetClassDashWeightGetter(broadAssetData.assetClassData, isUsedRescaledLong);
                        return {
                            ...prev,
                            [`${index}`]: getBroadAssetClassWeight(assetClassId)
                        };
                    }, broadAssetClass);
                    const detailAssetClasses = [];

                    // HYPO-1570,no need to display sub-item if the parent item includes only one sub-item.
                    const showSubItems =
                        Object.keys(detailAssetGroup.compItems).filter(detailAssetClassId => {
                            const detailAssetClass = detailAssetGroup.compItems[detailAssetClassId];
                            return detailAssetClass.groupId === assetClassId;
                        }).length > 1;

                    Object.keys(detailAssetGroup.compItems).forEach(detailAssetClassId => {
                        const detailAssetClass = detailAssetGroup.compItems[detailAssetClassId];
                        if (showSubItems && detailAssetClass.groupId === assetClassId) {
                            let detailAssetClassObj = {
                                assetClassId: detailAssetClassId,
                                name: detailAssetClass.className
                            };
                            detailAssetClassObj = detailAssetDatas.reduce((prev, detailAssetData, index) => {
                                const getDetailedAssetClassWeight = assetClassDashWeightGetter(detailAssetData.assetClassData, isUsedRescaledLong);
                                return {
                                    ...prev,
                                    [`${index}`]: getDetailedAssetClassWeight(detailAssetClassId)
                                };
                            }, detailAssetClassObj);
                            detailAssetClasses.push(detailAssetClassObj);
                        }
                    });

                    if (detailAssetClasses.length > 0) {
                        broadAssetClass.detailAssetClasses = detailAssetClasses.sort((a, b) => {
                            return detailAssetGroup.compItems[a.assetClassId].order - detailAssetGroup.compItems[b.assetClassId].order;
                        });
                    }
                    return broadAssetClass;
                });

            const rows = [];
            const columns = [];
            columns.push({
                text: "",
                value: "name"
            });
            detailAssetDatas.forEach((i, index) => {
                const text = getters.isSinglePortfolio && index <= 1 ? i.name.replace(/\d*\s*/g, "") : i.name;
                columns.push({
                    text,
                    value: index
                });
            });

            let totalRow = {
                name: "Total"
            };
            totalRow = detailAssetDatas.reduce((prev, i, index) => {
                let total = 0;
                if (!i.assetClassData) {
                    total = DASH;
                } else {
                    broadAssetClasses.forEach(broadAssetClass => {
                        total += broadAssetClass[index];
                    }, total);
                }
                return {
                    ...prev,
                    [`${index}`]: total
                };
            }, totalRow);

            rows.push(totalRow);

            broadAssetClasses.forEach((broadAssetClass, index) => {
                const detailAssetClasses = broadAssetClass.detailAssetClasses;
                delete broadAssetClass.detailAssetClasses;
                delete broadAssetClass.assetClassId;
                rows.push(broadAssetClass);
                if (typeof detailAssetClasses !== "undefined") {
                    detailAssetClasses.forEach(detailAssetClass => {
                        delete detailAssetClass.assetClassId;
                        rows.push({
                            ...detailAssetClass,
                            isChild: true
                        });
                    });
                }
            });
            return {
                rows: rows,
                columns: columns
            };
        } else {
            return {
                rows: [],
                columns: []
            };
        }
    },
    validBenchmarkData: (state, getters, rootState) => (data, index = 0) => {
        const isInvestmentFlow = parseInt(rootState.hypo.hypoType) === 2;
        const investmentSource = isInvestmentFlow ? data.filter(item => item.type === "Holding")[0] : data.filter(item => item.type === "Portfolio")[index];
        const benchmark = isInvestmentFlow
            ? data.find(item => item.type === "Benchmark")
            : data.find(item => item.type === "Benchmark" && item.id === investmentSource.id);
        const endDate = investmentSource.endDate.split("T")[0];
        if (benchmark && typeof benchmark.serials[endDate] === "undefined") {
            return false;
        }
        return true;
    },
    isSinglePortfolio: (state, getters, rootState, rootGetters) => {
        return parseInt(rootGetters["hypo/getType"]) === 0;
    },
    formatLabel: (state, getters) => label => {
        return getters.isSinglePortfolio ? label.replace(/\d*\s*/g, "") : label;
    }
};
const actions = {
    getChartData({ commit, rootGetters }, { fetch }) {
        return fetch({
            url: `/api/v1/Chart`,
            method: "POST",
            body: JSON.stringify(rootGetters.hypoInput)
        }).then(data => {
            commit(types.SET_CHART_DATA, data);
        });
    }
};

const mutations = {
    [types.RESET](state) {
        const initialData = initialState();
        Object.keys(initialData).forEach(key => {
            state[key] = initialData[key];
        });
    },
    [types.SET_CHART_DATA](state, data) {
        state.chartData = data;
    },
    [types.SET_RELOAD_CHART_DATA](state, data) {
        state.reloadChartData = data;
    }
};

export default {
    state,
    getters,
    mutations,
    actions
};
